import { z } from 'zod';
import { baseComponentSchema, imageCropAreaSchema } from '../component-base.schemas';

const hex6 = /^#[0-9A-Fa-f]{6}$/;
const rgbLike = /^rgba?\(\s*[^)]+\)$/i;

const hexColorSchema = z.string().regex(hex6, 'Invalid hex color');
/** Overlay is often semi-transparent; allow #RRGGBB or rgb()/rgba(). */
const overlayColorSchema = z
  .string()
  .refine(s => hex6.test(s) || rgbLike.test(s.trim()), 'Use #RRGGBB or rgb()/rgba()');

export const conversionPopupCustomColorsSchema = z
  .object({
    overlay: overlayColorSchema.optional(),
    panelBackground: hexColorSchema.optional(),
    headline: hexColorSchema.optional(),
    body: hexColorSchema.optional(),
    buttonBackground: hexColorSchema.optional(),
    buttonText: hexColorSchema.optional(),
    closeButton: hexColorSchema.optional(),
  })
  .optional();

/** Same crop shape as form-split/media-text images; nullable clears on save/API merge */
const conversionPopupCropSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  zoom: z.number(),
  aspect: z.number(),
  croppedAreaPixels: imageCropAreaSchema,
  croppedAreaPercent: imageCropAreaSchema,
  grayscale: z.boolean().optional(),
});

/** Editor-only: which UI preset was used; runtime ignores this. */
export const conversionPopupTriggerConfigModeSchema = z.enum(['smart', 'custom']);
export type ConversionPopupTriggerConfigMode = z.infer<
  typeof conversionPopupTriggerConfigModeSchema
>;

const conversionPopupDataSchemaInner = z.object({
  triggerConfigMode: conversionPopupTriggerConfigModeSchema.optional(),
  contentPurpose: z.enum(['offer', 'lead_magnet', 'announcement', 'exit_message']).default('offer'),
  headline: z.string().min(1, 'Headline is required').max(200, 'Headline too long'),
  body: z.string().max(2000).optional(),
  primaryCtaLabel: z.string().max(100).optional(),
  primaryCtaUrl: z.string().optional(),
  primaryCtaTarget: z.enum(['_self', '_blank']).default('_self').optional(),
  /** Optional dismiss button (e.g. "No thanks"). Closes the popup; no navigation. */
  secondaryCtaLabel: z.string().max(100).optional(),
  /**
   * When true in custom mode, user must enter secondaryCtaLabel before save.
   * Runtime ignores this; only secondaryCtaLabel controls the visible button.
   */
  dismissButtonEnabled: z.boolean().default(false),
  contentAlignment: z.enum(['left', 'center', 'right']).default('left'),
  imageUrl: z.string().optional(),
  crop: conversionPopupCropSchema.nullable().optional(),
  exitIntentDesktop: z.boolean().default(false),
  timeDelaySeconds: z.number().int().min(0).max(3600).nullable().optional(),
  scrollDepthPercent: z.number().int().min(1).max(100).nullable().optional(),
  manualTriggerId: z.string().max(80).nullable().optional(),
  minDelayAfterLoadMs: z.number().int().min(0).max(120000).default(5000),
  pageSlugs: z.array(z.string().max(255)).default([]),
  showOncePerSession: z.boolean().default(true),
  excludeAfterConversion: z.boolean().default(false),
  conversionFormComponentIds: z.array(z.string().uuid()).default([]),
  mobileMode: z.enum(['disabled', 'bottom_sheet', 'fullscreen']).default('bottom_sheet'),
  maxViewportWidthForMobile: z.number().int().min(320).max(1200).default(768),
  priority: z.number().int().min(0).max(1000).default(0),
  campaignKey: z.string().max(64).optional(),
  customColors: conversionPopupCustomColorsSchema,
});

export const conversionPopupDataSchema = conversionPopupDataSchemaInner.superRefine((data, ctx) => {
  if (
    data.triggerConfigMode !== 'smart' &&
    data.dismissButtonEnabled &&
    !(data.secondaryCtaLabel ?? '').trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter text for the dismiss button, or uncheck "Show a dismiss button".',
      path: ['secondaryCtaLabel'],
    });
  }
});

export const conversionPopupComponentSchema = baseComponentSchema.extend({
  type: z.literal('conversion-popup'),
  data: conversionPopupDataSchema,
});

export type ConversionPopupCustomColors = z.infer<typeof conversionPopupCustomColorsSchema>;
export type ConversionPopupData = z.infer<typeof conversionPopupDataSchema>;
export type ConversionPopupComponent = z.infer<typeof conversionPopupComponentSchema>;
